import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { IoGiftOutline } from '@react-icons/all-files/io5/IoGiftOutline';
import { BucketLogData } from '@bucketplace/bucket-log';
import { getProductPriceInfo, Product } from '@bucketplace/common';
import { IntersectionObserver } from '@bucketplace/design-system';

import { RankingStoreItemFilter } from '@/components/RankingStoreItemList/RankingStoreItemFilter';
import { RankingStoreItemList, RankingStoreItemListActions } from '@/components/RankingStoreItemList/RankingStoreItemList';

import { EditorPlaceholder } from '../../components';
import { Exhibitions } from '../../components/Exhibitions';
import { ColType, ExhibitionItem } from '../../components/Exhibitions/types';
import { BestRankingStoreItemArgs, StoreItemFilter, useEditorInfoContext, useStoreItemsContext } from '../../contexts';
import { BadgeType, ProductListSelectionType, ProductTextBadge, RankingFilterType } from './types';

export interface ProductListProps {
  title: string;
  subtitle: string;
  /**
   * 상품 선택 방식
   * @default 'MANUAL'
   */
  selectionType: ProductListSelectionType;
  /**
   * @default 'CATEGORY'
   */
  rankingFilterType: RankingFilterType;
  /** 상품 목록 유형 */
  type: ColType;
  /** 상품 뱃지 유형 */
  badgeType: 'none' | BadgeType;
  /** 구매하기 버튼 사용 여부 */
  useBuyButton: boolean;
  /** 뱃지와 합쳐진 상품 목록 */
  products: ProductTextBadge[];
  logData?: BucketLogData;
}

export function ProductList({
  title,
  subtitle,
  selectionType,
  rankingFilterType,
  type,
  badgeType,
  useBuyButton,
  products,
  logData,
}: ProductListProps) {
  const [selectedStoreItemFilter, setSelectedStoreItemFilter] = useState<StoreItemFilter | null>(null);
  const [storeItemFilters, setStoreItemFilters] = useState<StoreItemFilter[]>([]);
  const [storeItems, setStoreItems] = useState<ExhibitionItem[]>([]);
  const [showPersonalizedFallback, setShowPersonalizedFallback] = useState(false);
  const {
    getStoreItems,
    getBestRankingStoreItems,
    getPersonalizedStoreItems,
    renderPersonalizedStoreItemsFallback,
    getStoreItemHref,
    linkComponent,
    experimentType,
  } = useStoreItemsContext();
  const { isInitialLoadAllContent } = useEditorInfoContext();
  const rankingStoreItemListActions = useRef<RankingStoreItemListActions>(null);

  const concatStoreData = (badgeList: ProductTextBadge[], addedStoreItems: Product[]): void => {
    const newStoreItems: ExhibitionItem[] = badgeList.map((badge) => {
      const findStoreItem = addedStoreItems.find((storeItem) => storeItem.id === badge.id) ||
        storeItems.find((storeItem) => storeItem.id === badge.id);

      if (findStoreItem) {
        return {
          ...findStoreItem,
          ...badge,
          // FIXME: 오시즌위크 상품명 실시간 반영을 위해 임시로 storeItem name 사용
          name: findStoreItem.name,
          getStoreItemHref,
          linkComponent,
        };
      }
      return null;
    }).filter(Boolean) as ExhibitionItem[];

    setStoreItems(newStoreItems);
  };

  const getProducts = useCallback(
    async (args?: { ranking?: Omit<BestRankingStoreItemArgs, 'type'> }): Promise<void> => {
      if (selectionType === 'PERSONALIZED') {
        const newStoreItems = await getPersonalizedStoreItems?.();
        // null은 비로그인 등으로 개인화 상품을 조회할 수 없는 상태
        setShowPersonalizedFallback(newStoreItems === null);
        const textBadges = newStoreItems?.map<ProductTextBadge>((item) => ({
          id: item.id,
          textBadgeList: [],
        }));
        concatStoreData(textBadges ?? [], newStoreItems ?? []);
        return;
      }

      if (selectionType === 'RANKING') {
        const resp = await getBestRankingStoreItems?.({
          ...args?.ranking,
          type: rankingFilterType,
        });
        const textBadges = resp?.items?.map<ProductTextBadge>((item) => ({
          id: item.id,
          textBadgeList: [],
        }));

        if (!args?.ranking?.filter) {
          setStoreItemFilters(resp?.chips ?? []);
        }

        concatStoreData(textBadges ?? [], resp?.items ?? []);
        return;
      }

      // inspector에서 빈 id들을 제외한 텍스트 뱃지 리스트들
      const existTextBadgeList = products.filter((product) => product.id);
      const newIds = existTextBadgeList
        .filter(({ id }) => !storeItems.some((storeItem) => storeItem.id === id))
        .map(({ id }) => id?.toString() ?? '') as string[];

      const newStoreItems = await getStoreItems?.(newIds);

      concatStoreData(existTextBadgeList, newStoreItems ?? []);
    },
    [products, selectionType, rankingFilterType],
  );

  useEffect(() => {
    if (isInitialLoadAllContent) {
      getProducts({
        ranking: { filter: selectedStoreItemFilter },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectionType]);

  const header = title.trim() || subtitle.trim()
    ? (
      <Header>
        { title.trim() && (
          <Title>{ title }</Title>
        ) }

        { subtitle.trim() && (
          <Subtitle>{ subtitle }</Subtitle>
        ) }
      </Header>
    )
    : null;

  if (products.length === 0 && selectionType === 'MANUAL') {
    return (
      <Root>
        { header }
        <EditorPlaceholder
          icon={<IoGiftOutline />}
          message="상품을 추가해주세요."
        />
      </Root>
    );
  }

  const ExhibitionElement = selectionType === 'RANKING'
    ? (
      <>
        <RankingStoreItemFilter
          value={selectedStoreItemFilter}
          options={storeItemFilters}
          onChange={(newValue) => {
            setSelectedStoreItemFilter(newValue);
            getProducts({
              ranking: {
                filter: newValue,
              },
            });
            rankingStoreItemListActions.current?.resetScroll();
          }}
        />

        <RankingStoreItemList
          actions={rankingStoreItemListActions}
          items={storeItems.map((storeItem) => {
            const { showDiscountRate, discountRate, price } = getProductPriceInfo(storeItem);

            return {
              productName: storeItem.name,
              productImageUrl: storeItem.originalImageUrl,
              productDiscount: showDiscountRate ? discountRate : undefined,
              productPrice: price,
              href: getStoreItemHref?.(storeItem.id),
              logData,
              linkComponent,
            };
          })}
        />
      </>
    )
    : (
      <Exhibitions
        disableVerticalPadding={!!header}
        storeItemList={storeItems}
        colType={selectionType === 'MANUAL' ? type : 'carousel'}
        badgeType={selectionType === 'MANUAL' ? badgeType : 'none'}
        useBuyButton={selectionType === 'MANUAL' ? useBuyButton : false}
        logData={logData}
        experimentType={experimentType}
      />
    );

  const personalizedFallback = selectionType === 'PERSONALIZED' && showPersonalizedFallback
    ? renderPersonalizedStoreItemsFallback?.() ?? null
    : null;

  const emptyElement = personalizedFallback
    ? (
      <Root>
        { header }
        { personalizedFallback }
      </Root>
    )
    : null;

  if (isInitialLoadAllContent) {
    if (storeItems.length === 0) {
      return emptyElement;
    }

    return (
      <Root>
        { header }
        { ExhibitionElement }
      </Root>
    );
  }

  return (
    <IntersectionObserver onIntersect={getProducts}>
      { storeItems.length === 0
        ? emptyElement
        : (
          <Root>
            { header }
            { ExhibitionElement }
          </Root>
        ) }
    </IntersectionObserver>
  );
}

const Root = styled.div`
  margin: 0;
`;

const Header = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  color: ${(props) => props.theme.colors.foreground};
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  color: ${(props) => props.theme.colors.foregroundWeak};
`;
